import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { CHART_COLORS, COLORS, STAT_CARD_STYLES, glassCardSx, chartPaperSx, tableHeaderSx, tableRowSx, overdueRowSx, tooltipPaperSx, surfaceInsetSx, makeXAxisLabel, makeYAxisLabel, makeYAxisLabelRight, CHART_MARGINS } from './clientPortalTheme';
import ClientPortalExpandableChart from './ClientPortalExpandableChart';

const PAPER_MIN_HEIGHT = 480;

const formatChartDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatHours = (value) => (value != null ? Number(value).toFixed(1) : '—');
const formatCount = (value) => (value != null ? Number(value).toLocaleString() : '—');

const UtilizationTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};

  const rows = [
    { label: 'Airframe Hours', value: formatHours(point.tsnHours), show: point.tsnHours != null },
    { label: 'Block Time (period)', value: formatHours(point.flightHours), show: point.flightHours != null },
    { label: 'Cumulative Block Time', value: formatHours(point.cumulativeFlightHours), show: point.cumulativeFlightHours != null },
    { label: 'Landings (period)', value: formatCount(point.landings), show: point.landings != null },
    { label: 'Total Landings (Tech Log)', value: formatCount(point.tsnLandings ?? point.cumulativeLandings), show: (point.tsnLandings ?? point.cumulativeLandings) != null }
  ].filter((row) => row.show);

  return (
    <Paper elevation={4} sx={{ minWidth: 200, ...tooltipPaperSx }}>
      <Typography variant="caption" fontWeight={700} display="block" gutterBottom>
        {formatChartDate(label)}
      </Typography>
      {rows.map((row) => (
        <Box key={row.label} display="flex" justifyContent="space-between" gap={2} sx={{ py: 0.25 }}>
          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>{row.label}</Typography>
          <Typography variant="caption" fontWeight={600}>{row.value}</Typography>
        </Box>
      ))}
    </Paper>
  );
};

const chartMargins = CHART_MARGINS.default;
const chartMarginsDualY = CHART_MARGINS.dualY;
const axisTick = { fontSize: 12, fill: COLORS.textMuted };
const legendStyle = { fontSize: 13, paddingTop: 8, color: COLORS.textSecondary };
const denseAxisProps = (count) => (count > 8
  ? { angle: -35, textAnchor: 'end', height: 80, interval: count > 16 ? Math.floor(count / 12) : 0 }
  : { height: 56 });

const ClientUtilizationCharts = ({ utilization, loading }) => {
  const series = utilization?.series || [];

  const summary = useMemo(() => {
    if (!series.length) return null;
    const last = series[series.length - 1];
    const totalPeriodHours = series.reduce((sum, p) => sum + (p.flightHours || 0), 0);
    const totalPeriodLandings = series.reduce((sum, p) => sum + (p.landings || 0), 0);
    const tsnPoints = series.filter((p) => p.tsnHours != null);

    const tsnLandingPoints = series.filter((p) => p.tsnLandings != null || p.cumulativeLandings != null);
    const latestTsnLandings = tsnLandingPoints.length
      ? (tsnLandingPoints[tsnLandingPoints.length - 1].tsnLandings
        ?? tsnLandingPoints[tsnLandingPoints.length - 1].cumulativeLandings)
      : null;

    return {
      dataPoints: series.length,
      totalPeriodHours,
      totalPeriodLandings,
      latestCumulativeHours: last.cumulativeFlightHours,
      latestCumulativeLandings: latestTsnLandings,
      latestTsn: tsnPoints.length ? tsnPoints[tsnPoints.length - 1].tsnHours : null
    };
  }, [series]);

  if (loading) return null;

  if (series.length === 0) {
    return <Alert severity="info" sx={{ borderRadius: 3, ...glassCardSx, color: COLORS.textSecondary }}>No utilization data available yet for this aircraft.</Alert>;
  }

  const tsnData = series.filter((p) => p.tsnHours != null);
  const flightData = series.filter((p) => p.cumulativeFlightHours != null || p.flightHours != null);
  const landingData = series.filter((p) => p.landings != null || p.tsnLandings != null || p.cumulativeLandings != null);

  const statCards = [
    utilization?.latestTsn && {
      key: 'periodValue',
      label: 'Latest Hours (Tech Log)',
      value: utilization.latestTsn.periodValue || '—',
      bg: STAT_CARD_STYLES.indigo
    },
    utilization?.latestTsn && {
      key: 'asOnDate',
      label: 'Tech Log As On',
      value: utilization.latestTsn.asOnDate
        ? new Date(utilization.latestTsn.asOnDate).toLocaleDateString()
        : '—',
      bg: STAT_CARD_STYLES.indigoDark
    },
    utilization?.latestTsn?.landings != null && {
      key: 'techLogLandings',
      label: 'Landings (Tech Log)',
      value: formatCount(utilization.latestTsn.landings),
      bg: STAT_CARD_STYLES.indigoLight
    },
    {
      key: 'currentFlightHours',
      label: 'Total Flight Hours',
      value: utilization?.currentFlightHours ?? summary?.latestCumulativeHours ?? '—',
      bg: STAT_CARD_STYLES.grey
    },
    summary?.latestCumulativeLandings != null && {
      key: 'cumulativeLandings',
      label: 'Total Landings (Tech Log)',
      value: formatCount(summary.latestCumulativeLandings),
      bg: STAT_CARD_STYLES.greyLight
    },
    {
      key: 'dataPoints',
      label: 'Data Points',
      value: summary?.dataPoints ?? series.length,
      bg: STAT_CARD_STYLES.grey
    }
  ].filter(Boolean);

  const chartPaperSxLocal = { ...chartPaperSx, minHeight: PAPER_MIN_HEIGHT };

  const renderAirframeChart = ({ height, expanded }) => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={tsnData} margin={chartMargins}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.glassBorder} />
        <XAxis
          dataKey="date"
          tick={axisTick}
          tickFormatter={formatShortDate}
          label={makeXAxisLabel('Date (Approved Tech Log Entry)')}
          {...denseAxisProps(tsnData.length)}
        />
        <YAxis
          tick={axisTick}
          tickFormatter={formatHours}
          width={56}
          label={makeYAxisLabel('Total Airframe Hours (decimal hrs)')}
        />
        <Tooltip content={<UtilizationTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        <Line
          type="monotone"
          dataKey="tsnHours"
          stroke={CHART_COLORS[0]}
          strokeWidth={expanded ? 3 : 2.5}
          name="Airframe Hours"
          dot={{ fill: CHART_COLORS[0], r: expanded ? 5 : 4 }}
          activeDot={{ r: expanded ? 7 : 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderBlockTimeChart = ({ height, expanded }) => (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={flightData} margin={chartMarginsDualY}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.glassBorder} />
        <XAxis
          dataKey="date"
          tick={axisTick}
          tickFormatter={formatShortDate}
          label={makeXAxisLabel('Date (Tech Log Sector Activity)')}
          {...denseAxisProps(flightData.length)}
        />
        <YAxis
          yAxisId="left"
          tick={axisTick}
          tickFormatter={formatHours}
          width={56}
          label={makeYAxisLabel('Daily Block Time (decimal hrs)')}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={axisTick}
          tickFormatter={formatHours}
          width={56}
          label={makeYAxisLabelRight('Cumulative Block Time (decimal hrs)')}
        />
        <Tooltip content={<UtilizationTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        <Bar
          yAxisId="left"
          dataKey="flightHours"
          fill={CHART_COLORS[2]}
          name="Block Time"
          radius={[4, 4, 0, 0]}
          maxBarSize={expanded ? 64 : 48}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="cumulativeFlightHours"
          stroke={CHART_COLORS[1]}
          strokeWidth={expanded ? 3 : 2.5}
          name="Cumulative Hours"
          dot={{ fill: CHART_COLORS[1], r: expanded ? 4 : 3 }}
          activeDot={{ r: expanded ? 6 : 5 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );

  const renderLandingsChart = ({ height, expanded }) => (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={landingData} margin={chartMarginsDualY}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.glassBorder} />
        <XAxis
          dataKey="date"
          tick={axisTick}
          tickFormatter={formatShortDate}
          label={makeXAxisLabel('Date (Tech Log Sector Activity)')}
          {...denseAxisProps(landingData.length)}
        />
        <YAxis
          yAxisId="left"
          allowDecimals={false}
          tick={axisTick}
          width={48}
          label={makeYAxisLabel('Daily Landings (count)')}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          allowDecimals={false}
          tick={axisTick}
          width={48}
                    label={makeYAxisLabelRight('Total Landings — Tech Log (count)')}
        />
        <Tooltip content={<UtilizationTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        <Bar
          yAxisId="left"
          dataKey="landings"
          fill={CHART_COLORS[3]}
          name="Landings"
          radius={[4, 4, 0, 0]}
          maxBarSize={expanded ? 64 : 48}
        />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="tsnLandings"
                    connectNulls
                    stroke={CHART_COLORS[4]}
                    strokeWidth={expanded ? 3 : 2.5}
                    name="Total Landings (Tech Log)"
                    dot={{ fill: CHART_COLORS[4], r: expanded ? 4 : 3 }}
                    activeDot={{ r: expanded ? 6 : 5 }}
                  />
      </ComposedChart>
    </ResponsiveContainer>
  );

  return (
    <Box>
      {statCards.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.key}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  background: card.bg,
                  color: COLORS.white,
                  boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ position: 'absolute', width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', top: -20, right: -10 }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{card.label}</Typography>
                <Typography variant="h5" fontWeight={800}>{card.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={3}>
        {tsnData.length > 0 && (
          <Grid item xs={12}>
            <ClientPortalExpandableChart
              title="Airframe Hours Over Time (Tech Log)"
              description={`${tsnData.length} approved tech log entries${summary?.latestTsn != null ? ` · Latest: ${formatHours(summary.latestTsn)} hrs` : ''}`}
              paperSx={chartPaperSxLocal}
            >
              {renderAirframeChart}
            </ClientPortalExpandableChart>
          </Grid>
        )}

        {flightData.length > 0 && (
          <Grid item xs={12}>
            <ClientPortalExpandableChart
              title="Flight Hours (Tech Log Block Time)"
              description={`Daily block time from approved tech log sectors (HHMM) and cumulative total${summary?.totalPeriodHours > 0 ? ` · ${formatHours(summary.totalPeriodHours)} hrs logged across ${flightData.length} entries` : ''}`}
              paperSx={chartPaperSxLocal}
            >
              {renderBlockTimeChart}
            </ClientPortalExpandableChart>
          </Grid>
        )}

        {landingData.length > 0 && (
          <Grid item xs={12}>
            <ClientPortalExpandableChart
              title="Landings"
              description={`Daily landings from tech log sectors; total from carried-forward landings${summary?.totalPeriodLandings > 0 ? ` · ${formatCount(summary.totalPeriodLandings)} landings across ${landingData.length} entries` : ''}`}
              paperSx={chartPaperSxLocal}
            >
              {renderLandingsChart}
            </ClientPortalExpandableChart>
          </Grid>
        )}
      </Grid>

      <Paper sx={{ mt: 3, ...glassCardSx, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, ...surfaceInsetSx, borderRadius: 0, borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: `1px solid ${COLORS.glassBorder}` }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: COLORS.text }}>Utilization History</Typography>
          <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
            Full timeline of tech-log airframe hours, block time, and landings
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={tableHeaderSx}>
                <TableCell>Date</TableCell>
                <TableCell align="right">Airframe Hours</TableCell>
                <TableCell align="right">Block Time</TableCell>
                <TableCell align="right">Cumulative Hours</TableCell>
                <TableCell align="right">Landings (Period)</TableCell>
                <TableCell align="right">Total Landings (Tech Log)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...series].reverse().map((row) => (
                <TableRow key={row.date} hover sx={tableRowSx}>
                  <TableCell>{formatChartDate(row.date)}</TableCell>
                  <TableCell align="right">{formatHours(row.tsnHours)}</TableCell>
                  <TableCell align="right">{formatHours(row.flightHours)}</TableCell>
                  <TableCell align="right">{formatHours(row.cumulativeFlightHours)}</TableCell>
                  <TableCell align="right">{formatCount(row.landings)}</TableCell>
                  <TableCell align="right">{formatCount(row.tsnLandings ?? row.cumulativeLandings)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ClientUtilizationCharts;
